import React from 'react';
import {
  Box,
  Container,
  SimpleGrid,
  Text,
  HStack,
  VStack,
  Link,
  Flex,
  Image,
  Divider,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiMail } from 'react-icons/fi';
import { useLegalLang } from '../LegalLangContext';
import { APP_META } from '../content/appMeta';
import waeboxIcon from 'assets/img/waebox-icon.png';
import { PLATFORM_LIST } from '../content/platforms';

const LegalFooter = () => {
  const { t } = useLegalLang();
  const year = new Date().getFullYear();

  return (
    <Box as="footer" bg="gray.50" borderTop="1px solid" borderColor="gray.200" mt={16}>
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 10, md: 12 }}>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={{ base: 8, md: 10 }}>
          {/* About */}
          <VStack align="flex-start" spacing={3} gridColumn={{ md: 'span 2' }}>
            <HStack spacing={2}>
              <Image
                src={waeboxIcon}
                alt={`${APP_META.name} logo`}
                boxSize="32px"
                borderRadius="8px"
              />
              <Text fontWeight="700" color="text.primary">
                {APP_META.name}
              </Text>
            </HStack>
            <Text fontSize="sm" color="text.secondary" lineHeight="1.6" maxW="md">
              {t('footerAbout')}
            </Text>
            <HStack spacing={2} pt={1}>
              <FiMail size={14} color="#6B7280" />
              <Link
                href={`mailto:${APP_META.contactEmail}`}
                fontSize="sm"
                color="text.secondary"
                _hover={{ color: 'brand.700' }}
              >
                {APP_META.contactEmail}
              </Link>
            </HStack>
          </VStack>

          {/* Legal */}
          <VStack align="flex-start" spacing={3}>
            <Text
              fontSize="xs"
              fontWeight="700"
              color="text.muted"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              {t('footerLegal')}
            </Text>
            <VStack align="flex-start" spacing={2}>
              <Link
                as={RouterLink}
                to="/legal"
                fontSize="sm"
                color="text.secondary"
                _hover={{ color: 'brand.700' }}
              >
                {t('footerNavLegalIndex')}
              </Link>
            </VStack>
          </VStack>

          {/* Per-platform shortcuts */}
          <VStack align="flex-start" spacing={3}>
            <Text
              fontSize="xs"
              fontWeight="700"
              color="text.muted"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              {t('footerPlatforms')}
            </Text>
            <VStack align="flex-start" spacing={2}>
              {PLATFORM_LIST.slice(0, 6).map(p => (
                <Link
                  key={p.slug}
                  as={RouterLink}
                  to={`/legal/${p.slug}/terms`}
                  fontSize="sm"
                  color="text.secondary"
                  _hover={{ color: 'brand.700' }}
                >
                  {p.name}
                </Link>
              ))}
            </VStack>
          </VStack>
        </SimpleGrid>

        <Divider my={8} borderColor="gray.200" />

        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          gap={3}
        >
          <Text fontSize="xs" color="text.muted">
            © {year} {APP_META.legalEntity}. {t('copyright')}
          </Text>
          <HStack spacing={4}>
            <Link
              href={APP_META.websiteUrl}
              fontSize="xs"
              color="text.muted"
              _hover={{ color: 'brand.700' }}
              isExternal
            >
              {APP_META.websiteUrl.replace(/^https?:\/\//, '')}
            </Link>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
};

export default LegalFooter;
